import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { BankAccountOutlinedIcon, BankCardOutlinedIcon, InfoOutlinedIcon } from '@getgo/chameleon-icons/react';
import {
  Alert,
  Button,
  Checkbox,
  RadioButton,
  RadioGroup,
  Skeleton,
  Typography,
} from '@getgo/chameleon-web-react-wrapper';

import { ReactComponent as Paypal } from 'assets/Paypal.svg';
import { ReactComponent as RazorpayIcon } from 'assets/RazorpayIcon.svg';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  directDebitType,
  isPaymentMethodPresent,
  paymentMethodsAdditionCount,
  paymentMethodsIsLoading,
  paymentMethodTypes,
  setPayNowIntent,
} from 'modules/payment-methods';
import { hasG2RProducts, sessionInvoiceNumber } from 'modules/session-details';
import Track, { PayNowEventNames } from 'modules/tracking';
import { getPaymentTrackingType } from 'modules/tracking/tracking-module';
import {
  DD_TYPES,
  PARTNER_TYPES,
  PAYMENT_METHOD_TYPES,
  PAYMENT_METHODS_TYPES,
  PAYMENT_METHODS_TYPES_URL,
  SEARCH_PARAMS,
} from 'utils/constants';
import st from 'utils/shared-translations';

import './payment-method-options.css';

const PaymentMethodOptions: FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const pathName = window.location.pathname;

  const isPaymentMethodAdd = pathName.includes('paymentmethod/add');

  const selectedSessionHasG2RProducts = useAppSelector(hasG2RProducts);

  const selectedPaymentMethodsIsLoading = useAppSelector(paymentMethodsIsLoading);

  const partnerType = localStorage.getItem(SEARCH_PARAMS.partnerType);

  const [paymentMethod, setPaymentMethod] = useState('');
  const [isChecked, setChecked] = useState(false);

  const selectedPaymentTypes = useAppSelector(paymentMethodTypes);
  const selectedPaymentMethodsAdditionCount = useAppSelector(paymentMethodsAdditionCount);
  const selectedDirectDebitType = useAppSelector(directDebitType);
  const selectedPaymentMethodIsPresent = useAppSelector(isPaymentMethodPresent);
  const selectedSessionInvoiceNumber = useAppSelector(sessionInvoiceNumber);

  const isAddPayment = (paymentMethodType: keyof typeof PAYMENT_METHOD_TYPES) =>
    selectedPaymentTypes.some((type) => PAYMENT_METHOD_TYPES[paymentMethodType].includes(type)) &&
    !!selectedPaymentMethodsAdditionCount[paymentMethodType];

  const handleContinue = () => {
    Track(PayNowEventNames.paynowContinueAddNewMethod, {
      paymentType: getPaymentTrackingType(paymentMethod, selectedPaymentTypes),
    });
    if (isPaymentMethodAdd) {
      const newPath = pathName.replace('/add', `/${PAYMENT_METHODS_TYPES_URL[paymentMethod]}`);
      navigate(newPath);
    } else dispatch(setPayNowIntent(paymentMethod));
  };

  const handlePaymentType = (event: any) => {
    const { value } = event.target;
    setPaymentMethod(value);
    setChecked(false);
  };

  const getDirectDebitLabel = () => {
    switch (selectedDirectDebitType) {
      case DD_TYPES.sepa:
        return intl.formatMessage(st['payment.direct.debit.sepa']);
      case DD_TYPES.bacs:
        return intl.formatMessage(st['payment.direct.debit.bacs']);
      case DD_TYPES.becs:
        return intl.formatMessage(st['payment.direct.debit.becs']);
      case DD_TYPES.pad:
        return intl.formatMessage(st['payment.direct.debit.pad']);
      default:
        return '';
    }
  };

  const getSelectedPaymentMethod = () => {
    if (paymentMethod) return paymentMethod;
    if (isAddPayment(PAYMENT_METHODS_TYPES.cc)) return PAYMENT_METHODS_TYPES.cc;
    if (isAddPayment(PAYMENT_METHODS_TYPES.dd)) return PAYMENT_METHODS_TYPES.dd;
    if (isAddPayment(PAYMENT_METHODS_TYPES.ach)) return PAYMENT_METHODS_TYPES.ach;
    if (isAddPayment(PAYMENT_METHODS_TYPES.paypal)) return PAYMENT_METHODS_TYPES.paypal;
    if (isAddPayment(PAYMENT_METHODS_TYPES.razorpay)) return PAYMENT_METHODS_TYPES.razorpay;
    return '';
  };

  /* Show checkbox only when
   * No existing payment method is present AND
   * Invoice number is present in the session (It's an Invoice Pay Now) AND
   * Selected payment method is not ACH or Direct Debit
   * */
  const isDisplayCheckbox =
    !selectedPaymentMethodIsPresent &&
    !!selectedSessionInvoiceNumber &&
    ![PAYMENT_METHODS_TYPES.ach, PAYMENT_METHODS_TYPES.dd].includes(paymentMethod as any);

  return (
    <>
      {selectedPaymentMethodsIsLoading ? (
        <Skeleton
          size="xxxlarge"
          className="payment-method-options__skeleton"
          data-testid="payment-method-options__skeleton"
        />
      ) : (
        <div data-testid="payment-method-options">
          <Typography tag="h1" variant="heading-medium">
            {intl.formatMessage(st['payment.methods.header'])}
          </Typography>
          {!selectedPaymentMethodIsPresent && (
            <Alert variant="info" icon={<InfoOutlinedIcon />} className="payment-method-options__alert">
              {intl.formatMessage(st['payment.method.add.new'])}
            </Alert>
          )}
          <RadioGroup
            className="payment-method-options"
            aria-label="Payment method options"
            value={getSelectedPaymentMethod()}
            onChange={handlePaymentType}
            data-testid="payment-method-options__radio-group"
          >
            {isAddPayment(PAYMENT_METHODS_TYPES.cc) && partnerType !== PARTNER_TYPES.parent && (
              <RadioButton
                className="payment-method-options__radio"
                value={PAYMENT_METHODS_TYPES.cc}
                aria-label="Credit card"
                data-testid="payment-method-options__cc"
              >
                <div className="payment-method-options__type">
                  <BankCardOutlinedIcon size="24px" />
                  <Typography tag="h3" variant="body-small-strong">
                    {intl.formatMessage(st['payment.credit.debit.card'])}
                  </Typography>
                </div>
              </RadioButton>
            )}
            {isAddPayment(PAYMENT_METHODS_TYPES.dd) && partnerType !== PARTNER_TYPES.parent && (
              <RadioButton
                className="payment-method-options__radio"
                value={PAYMENT_METHODS_TYPES.dd}
                aria-label="Direct debit"
                data-testid="payment-method-options__dd"
              >
                <div className="payment-method-options__type">
                  <BankAccountOutlinedIcon size="24px" />
                  <Typography tag="h3" variant="body-small-strong">
                    {getDirectDebitLabel()}
                  </Typography>
                </div>
              </RadioButton>
            )}
            {!selectedSessionHasG2RProducts &&
              isAddPayment(PAYMENT_METHODS_TYPES.ach) &&
              partnerType !== PARTNER_TYPES.parent && (
                <RadioButton
                  className="payment-method-options__radio"
                  value={PAYMENT_METHODS_TYPES.ach}
                  aria-label="ACH"
                  data-testid="payment-method-options__ach"
                >
                  <div className="payment-method-options__type">
                    <BankAccountOutlinedIcon size="24px" />
                    <Typography tag="h3" variant="body-small-strong">
                      {intl.formatMessage(st['payment.direct.debit.ach'])}
                    </Typography>
                  </div>
                </RadioButton>
              )}
            {!selectedSessionHasG2RProducts &&
              isAddPayment(PAYMENT_METHODS_TYPES.paypal) &&
              partnerType !== PARTNER_TYPES.parent && (
                <RadioButton
                  className="payment-method-options__radio"
                  value={PAYMENT_METHODS_TYPES.paypal}
                  aria-label="PayPal"
                  data-testid="payment-method-options__paypal"
                >
                  <div className="payment-method-options__type">
                    <Paypal />
                    <Typography tag="h3" variant="body-small-strong">
                      {intl.formatMessage(st['payment.paypal'])}
                    </Typography>
                  </div>
                </RadioButton>
              )}
            {isAddPayment(PAYMENT_METHODS_TYPES.razorpay) && partnerType !== PARTNER_TYPES.parent && (
              <RadioButton
                className="payment-method-options__radio"
                value={PAYMENT_METHODS_TYPES.razorpay}
                aria-label="razorpay"
                data-testid="payment-method-options__razorpay"
              >
                <div className="payment-method-options__type">
                  <RazorpayIcon />
                  <Typography tag="h3" variant="body-small-strong">
                    {intl.formatMessage(st['payment.razorpay'])}
                  </Typography>
                </div>
              </RadioButton>
            )}
          </RadioGroup>

          {isDisplayCheckbox && (
            <Checkbox
              current-checked={isChecked}
              onChange={() => setChecked(!isChecked)}
              className="payment-method-options__checkbox"
            >
              <Typography variant="body-small" color="type-color-secondary">
                {intl.formatMessage(st['payment.method.autorenewal'])}
              </Typography>
            </Checkbox>
          )}

          {partnerType !== PARTNER_TYPES.parent && (
            <Button
              data-testid="payment-method-options__continue"
              disabled={!paymentMethod || (isDisplayCheckbox && !isChecked)}
              size="large"
              fullWidth
              onClick={handleContinue}
              aria-label="Continue"
            >
              {intl.formatMessage(st['payment.methods.continue'])}
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export default PaymentMethodOptions;
